
import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import { format, addDays, startOfDay, addMinutes, isBefore, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import 'react-calendar/dist/Calendar.css'; // Default styling, we will override with Tailwind/custom styles if needed

const Scheduler = () => {
    // Helper to find next business day
    const getNextBusinessDay = (startDate) => {
        let date = addDays(startDate, 1);
        while (date.getDay() === 0 || date.getDay() === 6) {
            date = addDays(date, 1);
        }
        return startOfDay(date);
    };

    const { t } = useTranslation('scheduler');
    // State
    const [step, setStep] = useState('date-time'); // 'date-time' | 'details'
    const [selectedDate, setSelectedDate] = useState(() => getNextBusinessDay(new Date()));
    const [availableSlots, setAvailableSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null); // To distinguish empty slots from errors
    const [selectedSlot, setSelectedSlot] = useState(null); // The specific time slot selected
    const [formData, setFormData] = useState({ name: '', email: '', notes: '', platform: 'google_meet' });
    const [formErrors, setFormErrors] = useState({}); // { name: 'error msg', email: 'error msg', generic: 'msg' }
    const [submitStatus, setSubmitStatus] = useState(null); // null | 'submitting' | 'success' | 'error'

    // Configuration
    const WORK_START_HOUR = 10;
    const WORK_END_HOUR = 20;
    const SLOT_DURATION = 30; // minutes
    const BRAND_COLOR = '#073A29'; // AppTek Green

    // --- Logic ---

    const calculateSlots = React.useCallback((baseDate, busySlots) => {
        const slots = [];
        let currentTime = new Date(baseDate);
        currentTime.setHours(WORK_START_HOUR, 0, 0, 0);

        const endTime = new Date(baseDate);
        endTime.setHours(WORK_END_HOUR, 0, 0, 0);



        while (isBefore(currentTime, endTime)) {
            const slotEnd = addMinutes(currentTime, SLOT_DURATION);

            // Check if slot is within the next 24 hours (minimum notice)
            const minimumBookingTime = addDays(new Date(), 1);

            if (isBefore(currentTime, minimumBookingTime)) {
                currentTime = slotEnd;
                continue;
            }

            // Capture current time for the closure
            const slotStart = new Date(currentTime);

            // Check verification against busy slots
            const isBusy = busySlots.some(busy => {
                const busyStart = parseISO(busy.start);
                const busyEnd = parseISO(busy.end);
                return (
                    (slotStart >= busyStart && slotStart < busyEnd) ||
                    (slotEnd > busyStart && slotEnd <= busyEnd)
                );
            });

            if (!isBusy) {
                slots.push(slotStart);
            }

            currentTime = slotEnd;
        }
        setAvailableSlots(slots);
    }, []);

    const fetchAvailability = React.useCallback(async (date) => {
        setLoading(true);
        setFetchError(null);
        setAvailableSlots([]);
        setSelectedSlot(null); // Reset selection on date change

        const startStr = date.toISOString();
        const endStr = addDays(date, 1).toISOString();

        try {
            const response = await fetch(`https://updnimcfnxsjvlhpfxjj.supabase.co/functions/v1/calendar?start=${startStr}&end=${endStr}`);
            if (!response.ok) {
                const errText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errText}`);
            }

            const data = await response.json();

            if (data.busy) {
                calculateSlots(date, data.busy);
            }
        } catch (error) {
            console.error("Error fetching slots:", error);
            setFetchError(error.message);
        } finally {
            setLoading(false);
        }
    }, [calculateSlots]);

    useEffect(() => {
        fetchAvailability(selectedDate);
    }, [selectedDate, fetchAvailability]);

    const handleDateChange = (date) => {
        setStep('date-time');
        setSelectedDate(startOfDay(date));
    };

    const handleSlotClick = (slot) => {
        setSelectedSlot(slot);
    };

    const handleNext = () => {
        if (selectedSlot) {
            setStep('details');
        }
    };

    const handleBooking = async (e) => {
        e.preventDefault();

        // Reset errors
        setFormErrors({});

        // Validate
        const errors = {};
        if (!formData.name.trim()) errors.name = t('errors.nameRequired') || 'Name is required';
        if (!formData.email.trim()) errors.email = t('errors.emailRequired') || 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = t('errors.emailInvalid') || 'Invalid email address';

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setSubmitStatus('submitting');

        const slotStart = selectedSlot;
        const slotEnd = addMinutes(slotStart, SLOT_DURATION);

        try {
            const res = await fetch('https://updnimcfnxsjvlhpfxjj.supabase.co/functions/v1/calendar', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    start: slotStart.toISOString(),
                    end: slotEnd.toISOString(),
                    attendeeName: formData.name,
                    attendeeEmail: formData.email,
                    description: formData.notes,
                    platform: formData.platform
                })
            });

            if (res.ok) {
                setSubmitStatus('success');
            } else {
                setSubmitStatus('error');
                // If backend returns specific error, handle it here. For now generic.
                setFormErrors({ generic: t('errors.generic') || 'Something went wrong. Please try again.' });
            }
        } catch (err) {
            setSubmitStatus('error');
            setFormErrors({ generic: t('errors.generic') || 'Something went wrong. Please try again.' });
        }
    };

    // Tooltip Component
    const ErrorTooltip = ({ message }) => (
        <div className="group relative ml-2">
            <svg className="w-5 h-5 text-red-500 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                {message}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
            </div>
        </div>
    );

    // Custom stylings for react-calendar to match brand
    const calendarStyles = `
      .react-calendar { 
         border: none; 
         width: 100%;
         font-family: inherit;
      }
      .react-calendar__navigation {
         margin-bottom: 10px;
      }
      .react-calendar__navigation button {
         font-size: 1.5rem;
         min-width: 44px;
         min-height: 44px;
         display: flex;
         align-items: center;
         justify-content: center;
      }
      .react-calendar__navigation__arrow {
         font-size: 1.75rem;
         font-weight: bold;
         display: flex;
         align-items: center;
         justify-content: center;
      }
      .react-calendar__tile--active {
         background: ${BRAND_COLOR} !important;
         color: white !important;
      }
      .react-calendar__tile--now {
         background: #f0fdf4;
         color: ${BRAND_COLOR};
      }
      .react-calendar__tile:enabled:hover,
      .react-calendar__tile:enabled:focus {
         background: #e6fcf5;
         color: ${BRAND_COLOR};
      }
    `;

    // Success animation styles
    const successAnimationStyles = `
        @keyframes checkmark-draw {
            0% { stroke-dashoffset: 50; }
            100% { stroke-dashoffset: 0; }
        }
        @keyframes circle-scale {
            0% { transform: scale(0); opacity: 0; }
            50% { transform: scale(1.2); }
            100% { transform: scale(1); opacity: 1; }
        }
        @keyframes fade-up {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes checkmark-draw {
            0% { stroke-dashoffset: 50; }
            100% { stroke-dashoffset: 0; }
        }
            0% { transform: scale(0.8); opacity: 1; }
            100% { transform: scale(2.5); opacity: 0; }
        }
        .animate-checkmark {
            stroke-dasharray: 50;
            stroke-dashoffset: 50;
            animation: checkmark-draw 0.6s ease-out 0.3s forwards;
        }
        .animate-circle {
            animation: circle-scale 0.4s ease-out forwards;
        }
        .animate-fade-up-1 { animation: fade-up 0.5s ease-out 0.5s forwards; opacity: 0; }
        .animate-fade-up-2 { animation: fade-up 0.5s ease-out 0.7s forwards; opacity: 0; }
        .animate-fade-up-3 { animation: fade-up 0.5s ease-out 0.9s forwards; opacity: 0; }
        .animate-fade-up-4 { animation: fade-up 0.5s ease-out 1.1s forwards; opacity: 0; }
        .pulse-ring {
            animation: pulse-ring 1.5s ease-out infinite;
        }
        .pulse-ring {
            animation: pulse-ring 1.5s ease-out infinite;
        }
    `;

    // Auto-scroll to top on success
    useEffect(() => {
        if (submitStatus === 'success') {
            const element = document.getElementById('scheduler-success-card');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [submitStatus]);

    if (submitStatus === 'success') {
        return (
            <div className="max-w-2xl mx-auto my-12 relative">
                <style>{successAnimationStyles}</style>

                {/* Main Card */}
                <div id="scheduler-success-card" className="relative bg-white shadow-2xl rounded-3xl p-12 text-center border border-gray-100 mx-4">
                    {/* Animated Success Icon */}
                    <div className="relative w-24 h-24 mx-auto mb-8">
                        {/* Pulse rings */}
                        <div className="absolute inset-0 rounded-full bg-green-100 pulse-ring"></div>
                        <div className="absolute inset-0 rounded-full bg-green-100 pulse-ring" style={{ animationDelay: '0.5s' }}></div>

                        {/* Circle background */}
                        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#073A29] to-[#0d5c40] animate-circle shadow-lg"></div>

                        {/* Checkmark */}
                        <svg className="absolute inset-0 w-full h-full p-6" viewBox="0 0 24 24" fill="none">
                            <path
                                className="animate-checkmark"
                                d="M5 13l4 4L19 7"
                                stroke="white"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>

                    {/* Content */}
                    <h2 className="text-3xl font-bold text-gray-900 mb-2 animate-fade-up-1">
                        {t('success.title')}
                    </h2>
                    <p className="text-lg text-gray-500 mb-8 animate-fade-up-2">
                        {t('success.subtitle')}
                    </p>

                    {/* Meeting Details Card */}
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 mb-8 animate-fade-up-3">
                        <div className="flex items-center justify-center gap-4 mb-4">
                            <div className="w-12 h-12 rounded-full bg-[#073A29] flex items-center justify-center text-white font-bold text-lg">
                                {selectedSlot && format(selectedSlot, 'd')}
                            </div>
                            <div className="text-left">
                                <p className="font-semibold text-gray-900 text-lg">
                                    {selectedSlot && format(selectedSlot, 'EEEE, MMMM d, yyyy')}
                                </p>
                                <p className="text-[#073A29] font-medium">
                                    {selectedSlot && format(selectedSlot, 'HH:mm')} - {selectedSlot && format(addMinutes(selectedSlot, 30), 'HH:mm')}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center justify-center gap-3 text-gray-600">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            <span>{t('success.meetingLink', {
                                platform: {
                                    'google_meet': 'Google Meet',
                                    'teams': 'Microsoft Teams',
                                    'zoom': 'Zoom'
                                }[formData.platform] || 'Google Meet'
                            })}</span>
                        </div>
                    </div>

                    {/* Email Confirmation */}
                    <div className="flex items-center justify-center gap-2 text-gray-500 mb-8 animate-fade-up-3">
                        <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span>{t('success.emailSent')} <strong className="text-gray-700">{formData.email}</strong></span>
                    </div>

                    {/* Action Button */}
                    <button
                        onClick={() => window.location.reload()}
                        className="animate-fade-up-4 px-8 py-3 bg-[#073A29] text-white rounded-full font-semibold hover:bg-[#052e20] transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    >
                        {t('success.bookAnother')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-4 md:mx-auto my-12 bg-white shadow-2xl rounded-2xl overflow-hidden min-h-[650px] flex flex-col md:flex-row relative">
            <style>{calendarStyles}</style>

            {/* Left Sidebar: Branding / Info (Optional context, keeps layout balanced) */}
            <div className={`md:w-1/3 p-8 text-white flex flex-col justify-between`} style={{ backgroundColor: BRAND_COLOR }}>
                <div>
                    <p className="opacity-70 text-sm font-uppercase tracking-wider mb-2">{t('sidebar.company')}</p>
                    <h2 className="text-3xl font-bold mb-6">{t('sidebar.title')}</h2>
                    <div className="flex items-center gap-3 opacity-90 mb-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                        <span>{t('sidebar.duration')}</span>
                    </div>
                    <div className="flex items-center gap-3 opacity-90">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg>
                        <span>
                            {{
                                'google_meet': 'Google Meet',
                                'teams': 'Microsoft Teams',
                                'zoom': 'Zoom'
                            }[formData.platform] || 'Google Meet'}
                        </span>
                    </div>
                </div>

                <div className="mt-12">
                    <p className="opacity-80 text-sm leading-relaxed">
                        {t('sidebar.description')}
                    </p>
                </div>
            </div>

            {/* Right Content Area */}
            <div className="md:w-2/3 p-8 flex flex-col h-full">

                {step === 'date-time' && (
                    <>
                        <div className="flex flex-col h-full animate-in fade-in slide-in-from-right-4 duration-500 pb-16">
                            <h3 className="text-xl font-bold text-gray-800 mb-6 text-center">{t('dateTime.title')}</h3>

                            <div className="flex flex-col md:flex-row gap-8 flex-1 min-h-0">
                                {/* Calendar Column */}
                                <div className="flex-1">
                                    <Calendar
                                        onChange={handleDateChange}
                                        value={selectedDate}
                                        minDate={addDays(new Date(), 1)}
                                        className="border-0"
                                        prevLabel={<svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>}
                                        nextLabel={<svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>}
                                        prev2Label={null}
                                        next2Label={null}
                                        tileDisabled={({ date, view }) => view === 'month' && (date.getDay() === 0 || date.getDay() === 6)}
                                    />
                                </div>


                                {/* Time Slots Column */}
                                <div className="md:w-48 flex flex-col h-[400px]">
                                    <h4 className="font-medium text-gray-700 mb-4 bg-white pb-2">
                                        {format(selectedDate, 'EEEE, MMM d')}
                                    </h4>

                                    <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                                        {loading ? (
                                            <div className="space-y-3">
                                                {[1, 2, 3].map(i => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse"></div>)}
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                {availableSlots.length > 0 ? availableSlots.map((slot, idx) => {
                                                    const isSelected = selectedSlot && slot.getTime() === selectedSlot.getTime();
                                                    return (
                                                        <button
                                                            key={idx}
                                                            onClick={() => handleSlotClick(slot)}
                                                            className={`w-full py-3 px-2 rounded border border-[#073A29] font-medium text-sm transition-all duration-200 mb-2
                                                            ${isSelected
                                                                    ? 'bg-[#073A29] text-white shadow-md transform scale-[1.02]'
                                                                    : 'bg-white text-[#073A29] hover:bg-green-50'
                                                                }`}
                                                        >
                                                            {format(slot, 'HH:mm')}
                                                        </button>
                                                    );
                                                }) : (
                                                    <div className="text-gray-400 text-sm italic py-4">
                                                        {fetchError ? (
                                                            <span className="text-red-500">
                                                                {t('dateTime.error') || 'Error loading availability.'}
                                                                <br />
                                                                <span className="text-xs opacity-75">{fetchError}</span>
                                                            </span>
                                                        ) : (
                                                            t('dateTime.noSlots')
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="absolute bottom-8 left-8 right-8 md:left-auto md:right-8 md:w-64">
                            <button
                                onClick={handleNext}
                                disabled={!selectedSlot}
                                className={`w-full py-3 rounded-lg font-bold transition-all shadow-lg
                                 ${selectedSlot
                                        ? 'bg-[#073A29] text-white hover:bg-[#052e20] hover:shadow-xl transform hover:-translate-y-0.5'
                                        : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
                                    }`}
                            >
                                {t('dateTime.next')}
                            </button>
                        </div>
                    </>
                )}


                {step === 'details' && (
                    <>
                        <div className="flex flex-col h-full animate-in fade-in slide-in-from-right-4 duration-500">
                            <div className="mb-6 flex items-center justify-center relative">
                                <button
                                    onClick={() => setStep('date-time')}
                                    className="absolute left-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 text-gray-600"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
                                </button>
                                <h3 className="text-xl font-bold text-gray-800">{t('details.title')}</h3>
                            </div>

                            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-100">
                                <p className="text-sm text-gray-500 uppercase font-semibold">{t('details.selectedTime')}</p>
                                <p className="text-lg font-medium text-[#073A29]">
                                    {selectedSlot && format(selectedSlot, 'EEEE, MMMM d, yyyy • HH:mm')}
                                </p>
                            </div>

                            <form onSubmit={handleBooking} className="space-y-6 max-w-md mx-auto w-full pb-20 px-1">

                                {/* Platform Selection */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-3">{t('details.platform')}</label>
                                    <div className="grid grid-cols-3 gap-3">
                                        {[
                                            { id: 'google_meet', label: 'Google Meet', icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6l5.25 3.15-.75 1.23L11 11.25V7z" /></svg> }, // Placeholder icon
                                            { id: 'teams', label: 'Teams', icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" /></svg> }, // Placeholder icon
                                            { id: 'zoom', label: 'Zoom', icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M15 8v8H5V8h10m1-2H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4V7c0-.55-.45-1-1-1z" /></svg> } // Placeholder icon
                                        ].map(platform => (
                                            <button
                                                key={platform.id}
                                                type="button"
                                                onClick={() => setFormData({ ...formData, platform: platform.id })}
                                                className={`flex flex-col items-center justify-center p-3 rounded-lg border transition-all ${formData.platform === platform.id
                                                    ? 'border-[#073A29] bg-green-50 text-[#073A29] ring-1 ring-[#073A29]'
                                                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                                                    }`}
                                            >
                                                <span className="mb-1">{platform.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                        {t('details.name')} *
                                        {formErrors.name && <ErrorTooltip message={formErrors.name} />}
                                    </label>
                                    <input
                                        className={`w-full border p-3 rounded-lg outline-none transition-all
                                        ${formErrors.name
                                                ? 'border-red-500 text-red-900 focus:ring-2 focus:ring-red-500 focus:border-transparent'
                                                : 'border-gray-300 focus:ring-2 focus:ring-[#073A29] focus:border-transparent'
                                            }`}
                                        value={formData.name}
                                        onChange={e => {
                                            setFormData({ ...formData, name: e.target.value });
                                            if (formErrors.name) setFormErrors({ ...formErrors, name: null });
                                        }}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                        {t('details.email')} *
                                        {formErrors.email && <ErrorTooltip message={formErrors.email} />}
                                    </label>
                                    <input
                                        type="email"
                                        className={`w-full border p-3 rounded-lg outline-none transition-all
                                        ${formErrors.email
                                                ? 'border-red-500 text-red-900 focus:ring-2 focus:ring-red-500 focus:border-transparent'
                                                : 'border-gray-300 focus:ring-2 focus:ring-[#073A29] focus:border-transparent'
                                            }`}
                                        value={formData.email}
                                        onChange={e => {
                                            setFormData({ ...formData, email: e.target.value });
                                            if (formErrors.email) setFormErrors({ ...formErrors, email: null });
                                        }}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('details.notes')}</label>
                                    <textarea
                                        className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-[#073A29] focus:border-transparent outline-none min-h-[100px]"
                                        value={formData.notes}
                                        onChange={e => setFormData({ ...formData, notes: e.target.value })}
                                    ></textarea>
                                </div>

                                {formErrors.generic && (
                                    <div className="flex items-center justify-center text-red-500 text-sm mt-2">
                                        <ErrorTooltip message={formErrors.generic} />
                                        <span className="ml-1">{formErrors.generic}</span>
                                    </div>
                                )}
                            </form>
                        </div>

                        <div className="absolute bottom-8 left-8 right-8 md:left-auto md:right-8 md:w-64">
                            <button
                                onClick={handleBooking}
                                disabled={submitStatus === 'submitting'}
                                className={`w-full py-3 bg-[#073A29] text-white rounded-lg font-bold hover:bg-[#052e20] disabled:opacity-50 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5`}
                            >
                                {submitStatus === 'submitting' ? t('details.submitting') : t('details.submit')}
                            </button>
                        </div>
                    </>
                )
                }

            </div >
        </div >
    );
};

export default Scheduler;
